import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import Sidebar from './Sidebar';
import Header from './Header';
import PageHeader from './ui/PageHeader';
import CustomDropdown from './ui/CustomDropdown';
import AvatarUpload from './AvatarUpload';
import { useAdmin } from '../contexts/AdminContext';
import { useTheme } from '../contexts/ThemeContext';
import { useSidebar } from '../contexts/SidebarContext';
import { usePageTitle } from '../hooks/usePageTitle';
import { formatPlanDate } from '../utils/planDate';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Settings, User, Shield, CreditCard, Globe, AlertTriangle, Info,
  Eye, EyeOff, Save, Lock, Check, X, ExternalLink,
  Youtube, Plug, ChevronDown, ChevronUp, TestTube, Loader2, Sparkles,
} from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';

const SETTINGS_GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

const BACKEND_URL = import.meta.env.VITE_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ─── Plan icon/emoji helper ─── */
const getPlanIcon = (planType) => {
  const p = (planType || '').toLowerCase();
  if (p === 'basic') return '⚡';
  if (p === 'lite') return '🚀';
  if (p === 'pro') return '👑';
  if (p === 'plus') return '💎';
  if (p === 'trial') return '🧪';
  return '⚡';
};

/* ─── Status config for plan badge ─── */
const STATUS_CONFIG = {
  ativo:     { label: 'Ativo',      color: '#34D399', bg: 'rgba(52, 211, 153, 0.10)', border: 'rgba(52, 211, 153, 0.25)' },
  grace:     { label: 'Pendente',   color: '#FBBF24', bg: 'rgba(251, 191, 36, 0.10)', border: 'rgba(251, 191, 36, 0.25)' },
  expirado:  { label: 'Expirado',   color: '#F87171', bg: 'rgba(248, 113, 113, 0.10)', border: 'rgba(248, 113, 113, 0.25)' },
  cancelado: { label: 'Cancelado',  color: '#F87171', bg: 'rgba(248, 113, 113, 0.10)', border: 'rgba(248, 113, 113, 0.25)' },
  inativo:   { label: 'Inativo',    color: '#9CA3AF', bg: 'rgba(156, 163, 175, 0.10)', border: 'rgba(156, 163, 175, 0.25)' },
};

/* ─── Plan benefits for chips ─── */
const PLAN_BENEFITS = {
  basic: ['Gerador de Áudio IA', 'Sem fidelidade'],
  lite:  ['Gerador de Áudio IA', 'Sem fidelidade'],
  pro:   ['Canais ilimitados', 'Vídeos Virais', 'Nichos Virais', 'Gerador de SRT'],
  plus:  ['Canais ilimitados', 'Vídeos Virais', 'Nichos Virais', 'Suporte prioritário'],
  trial: ['Acesso completo', 'Período de teste'],
};

/* ─── Gemini models for API selector ─── */
const GEMINI_MODELS = [
  { id: 'gemini-2.0-flash',  name: 'Gemini 2.0 Flash',  desc: 'Gratuito · Recomendado' },
  { id: 'gemini-2.5-flash',  name: 'Gemini 2.5 Flash',  desc: 'Gratuito · Mais novo' },
  { id: 'gemini-1.5-flash',  name: 'Gemini 1.5 Flash',  desc: 'Gratuito · Legado' },
];

/* ═══════════════════════════════════════════════════════════════════ */
const ConfiguracoesNew = ({ onLogout }) => {
  usePageTitle('Configurações');
  const { subscriptionData, isSuperAdmin, isLoading } = useAdmin();
  const { theme, isDarkMode, toggleTheme } = useTheme();
  const { sidebarWidth, isMobile } = useSidebar();
  const { i18n, t } = useTranslation();
  const location = useLocation();

  /* ── Profile state ── */
  const [profileData, setProfileData] = useState({
    username: '', email: '', full_name: '', created_at: '', avatar_url: null,
  });
  const [profileLoading, setProfileLoading] = useState(false);

  /* ── Password state ── */
  const [passwordData, setPasswordData] = useState({
    current_password: '', new_password: '', confirm_password: '',
  });
  const [passwordLoading, setPasswordLoading] = useState(false);

  /* ── FASE 4: métodos de login (criar senha / vincular Google) — aditivo ── */
  const [loginMethods, setLoginMethods] = useState(null);  // {has_password, has_google}
  const [createPw, setCreatePw] = useState({ new_password: '', confirm_password: '' });
  const [createPwLoading, setCreatePwLoading] = useState(false);
  const [showCreatePw, setShowCreatePw] = useState(false);
  const [showCreatePwConfirm, setShowCreatePwConfirm] = useState(false);
  const [showCurrentPw, setShowCurrentPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [showConfirmPw, setShowConfirmPw] = useState(false);

  /* ── Preferences state (localStorage-backed) ── */
  const [notifNovidades, setNotifNovidades] = useState(() =>
    JSON.parse(localStorage.getItem('pref_notif_novidades') ?? 'true')
  );
  const [notifLembretes, setNotifLembretes] = useState(() =>
    JSON.parse(localStorage.getItem('pref_notif_lembretes') ?? 'true')
  );

  /* ── API Keys state — YouTube (Fase 4/5 da unificação) ──
     A chave é OPCIONAL (contribui para o pool global do sistema). O backend
     retorna SOMENTE versões mascaradas (AIza...k3Jd) — os inputs ficam vazios
     e a máscara vira placeholder; digitar um valor novo SUBSTITUI a chave. */
  const [ytKeys, setYtKeys] = useState({
    youtube_api_key_1: '', youtube_api_key_2: '', youtube_api_key_3: '',
    youtube_api_key_4: '', youtube_api_key_5: '',
  });
  const [ytKeysMasked, setYtKeysMasked] = useState({});
  const [ytKeysLoading, setYtKeysLoading] = useState(true);
  const [ytKeysSaving, setYtKeysSaving] = useState(false);
  const [showYtKeys, setShowYtKeys] = useState(false);
  const [showYtTutorial, setShowYtTutorial] = useState(false);

  /* ── API Keys state — Gemini ── */
  const [geminiKey, setGeminiKey] = useState('');
  const [geminiModel, setGeminiModel] = useState('');
  const [geminiLoading, setGeminiLoading] = useState(true);
  const [geminiSaving, setGeminiSaving] = useState(false);
  const [geminiTesting, setGeminiTesting] = useState(false);
  const [geminiTestResult, setGeminiTestResult] = useState(null);
  const [showGeminiKey, setShowGeminiKey] = useState(false);

  /* ── Danger zone state ── */
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetConfirmText, setResetConfirmText] = useState('');
  const [resetting, setResetting] = useState(false);

  /* ── Tab state ── */
  const [activeTab, setActiveTab] = useState('perfil');

  const TABS = [
    { key: 'perfil',       icon: <User size={15} />,         label: 'Perfil' },
    { key: 'seguranca',    icon: <Shield size={15} />,       label: 'Segurança' },
    { key: 'apis',         icon: <Plug size={15} />,         label: 'APIs' },
    { key: 'preferencias', icon: <Globe size={15} />,        label: 'Preferências' },
    { key: 'avancado',     icon: <AlertTriangle size={15} />, label: 'Avançado' },
  ];

  /* ═══ Data Fetching ═══ */
  useEffect(() => {
    fetchProfile();
    fetchYoutubeKeys();
    fetchGeminiSettings();
    fetchLoginMethods();
  }, []);

  // Hash navigation → switch to APIs tab
  useEffect(() => {
    if (location.hash === '#apis') {
      setActiveTab('apis');
    }
  }, [location.hash]);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/profile`);
      const d = response.data;
      setProfileData({
        username: d.username || '',
        email: d.email || '',
        full_name: d.full_name || '',
        created_at: d.created_at || '',
        avatar_url: d.avatar_url || null,
      });
    } catch {
      toast.error('Erro ao carregar perfil');
    }
  };

  /* ── FASE 4: métodos de login ── */
  const fetchLoginMethods = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setLoginMethods({
        has_password: !!response.data.has_password,
        has_google: !!response.data.has_google,
      });
    } catch {
      // silencioso: se falhar, os blocos novos simplesmente não aparecem
    }
  };

  const handleSetPassword = async (e) => {
    e.preventDefault();
    if (createPw.new_password !== createPw.confirm_password) {
      toast.error('As senhas não coincidem'); return;
    }
    if (createPw.new_password.length < 6) {
      toast.error('A senha deve ter no mínimo 6 caracteres'); return;
    }
    setCreatePwLoading(true);
    try {
      await axios.post(`${API}/auth/set-password`, { new_password: createPw.new_password });
      toast.success('Senha criada! Agora você também entra por email/senha.');
      setCreatePw({ new_password: '', confirm_password: '' });
      setLoginMethods((m) => ({ ...m, has_password: true }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar senha');
    } finally {
      setCreatePwLoading(false);
    }
  };

  const handleLinkGoogle = async (credentialResponse) => {
    try {
      await axios.post(`${API}/auth/link-google`, { credential: credentialResponse.credential });
      toast.success('Conta Google vinculada!');
      setLoginMethods((m) => ({ ...m, has_google: true }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Não foi possível vincular o Google');
    }
  };

  const fetchYoutubeKeys = async () => {
    try {
      // O backend agora retorna SÓ as versões mascaradas (sem plaintext).
      // Cache antigo em sessionStorage guardava plaintext — descartado.
      sessionStorage.removeItem('youtube_api_keys_cache');
      const response = await axios.get(`${API}/profile/youtube-keys`);
      setYtKeysMasked({
        youtube_api_key_1: response.data.youtube_api_key_1_masked || '',
        youtube_api_key_2: response.data.youtube_api_key_2_masked || '',
        youtube_api_key_3: response.data.youtube_api_key_3_masked || '',
        youtube_api_key_4: response.data.youtube_api_key_4_masked || '',
        youtube_api_key_5: response.data.youtube_api_key_5_masked || '',
      });
    } catch {
      // Silent — user may not have keys yet
    } finally {
      setYtKeysLoading(false);
    }
  };

  const fetchGeminiSettings = async () => {
    try {
      const response = await axios.get(`${API}/extension/ai/settings`);
      if (response.data.success && response.data.settings) {
        const s = response.data.settings;
        setGeminiModel(s.ai_model || '');
        setGeminiKey(s.ai_api_key || '');
      }
    } catch {
      // Silent — defaults are fine
    } finally {
      setGeminiLoading(false);
    }
  };

  /* ═══ Handlers ═══ */
  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setProfileLoading(true);
    try {
      await axios.put(`${API}/profile`, { full_name: profileData.full_name });
      toast.success('Perfil atualizado com sucesso!');
    } catch {
      toast.error('Erro ao atualizar perfil');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleAvatarChange = (avatarUrl) => {
    setProfileData((prev) => ({ ...prev, avatar_url: avatarUrl }));
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('As senhas não coincidem');
      return;
    }
    if (passwordData.new_password.length < 6) {
      toast.error('A nova senha deve ter no mínimo 6 caracteres');
      return;
    }
    setPasswordLoading(true);
    try {
      await axios.post(`${API}/profile/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      toast.success('Senha alterada com sucesso!');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setPasswordLoading(false);
    }
  };

  // "Esqueci minha senha" logado (aba Segurança): dispara o MESMO fluxo do
  // /forgot-password (e-mail com link de redefinição), sem precisar deslogar.
  // Necessário porque o app desktop só entra com e-mail+senha.
  const [forgotLoading, setForgotLoading] = useState(false);
  const handleForgotPassword = async () => {
    if (!profileData.email || forgotLoading) return;
    setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: profileData.email });
      toast.success(`Link de redefinição enviado para ${profileData.email}. Confira sua caixa de entrada (e o spam).`);
    } catch {
      toast.error('Erro ao enviar o e-mail de redefinição. Tente novamente.');
    } finally {
      setForgotLoading(false);
    }
  };

  const handleNotifChange = (key, value) => {
    localStorage.setItem(`pref_notif_${key}`, JSON.stringify(value));
    if (key === 'novidades') setNotifNovidades(value);
    if (key === 'lembretes') setNotifLembretes(value);
  };

  const handleResetData = async () => {
    setResetting(true);
    try {
      await axios.post(`${API}/reset-all-data`);
      toast.success('Todos os dados foram deletados com sucesso');
      setShowResetModal(false);
      setResetConfirmText('');
      setTimeout(() => window.location.reload(), 2000);
    } catch {
      toast.error('Erro ao resetar dados');
      setResetting(false);
    }
  };

  /* ═══ API Key Handlers ═══ */
  const handleSaveYoutubeKeys = async () => {
    // Envia SOMENTE campos digitados: input vazio = "manter a chave atual"
    // (o backend interpreta string vazia como apagar; com inputs que começam
    // vazios por causa do masked-only, mandar tudo apagaria as chaves).
    const payload = {};
    [1, 2, 3, 4, 5].forEach((n) => {
      const k = ytKeys[`youtube_api_key_${n}`];
      if (k && k.trim()) payload[`youtube_api_key_${n}`] = k.trim();
    });
    if (Object.keys(payload).length === 0) {
      toast.error(t('settings_apis.youtube_nothing_to_save'));
      return;
    }
    setYtKeysSaving(true);
    try {
      await axios.put(`${API}/profile/youtube-keys`, payload);
      toast.success(t('settings_apis.youtube_saved'));
      // Limpa inputs e recarrega as máscaras (agora incluem as novas chaves).
      setYtKeys({
        youtube_api_key_1: '', youtube_api_key_2: '', youtube_api_key_3: '',
        youtube_api_key_4: '', youtube_api_key_5: '',
      });
      setYtKeysLoading(true);
      fetchYoutubeKeys();
    } catch {
      toast.error(t('settings_apis.youtube_error_save'));
    } finally {
      setYtKeysSaving(false);
    }
  };

  const handleSaveGemini = async () => {
    setGeminiSaving(true);
    try {
      await axios.post(`${API}/extension/ai/settings`, {
        ai_provider: 'gemini',
        ai_model: geminiModel || null,
        ai_api_key: geminiKey || null,
      });
      toast.success(t('settings_apis.gemini_saved'));
    } catch {
      toast.error(t('settings_apis.gemini_error_save'));
    } finally {
      setGeminiSaving(false);
    }
  };

  const handleTestGemini = async () => {
    if (!geminiKey) return;
    setGeminiTesting(true);
    setGeminiTestResult(null);
    try {
      const response = await axios.post(`${API}/extension/ai/test-connection`, {
        prompt: 'Teste',
        provider: 'gemini',
        model: geminiModel || null,
        api_key: geminiKey,
      });
      setGeminiTestResult(response.data);
      if (response.data.success) toast.success(t('settings_apis.gemini_test_ok', { model: response.data.model }));
      else toast.error(response.data.error || t('settings_apis.gemini_test_fail'));
    } catch (error) {
      setGeminiTestResult({ success: false, error: error.response?.data?.error || error.message });
      toast.error(t('settings_apis.gemini_test_fail'));
    } finally {
      setGeminiTesting(false);
    }
  };

  /* ─── API Status Helpers ─── */
  // Conta pelas MÁSCARAS (chaves já salvas) — os inputs ficam vazios por design.
  const ytKeysCount = [1, 2, 3, 4, 5].filter(n => !!ytKeysMasked[`youtube_api_key_${n}`]).length;
  const ytKeysConfigured = ytKeysCount > 0;
  // Salvar habilita quando há ALGO digitado (chave nova a enviar).
  const hasTypedYtKey = [1, 2, 3, 4, 5].some(n => (ytKeys[`youtube_api_key_${n}`] || '').trim());
  const geminiConfigured = !!(geminiKey && geminiKey.trim());

  /* ═══ Helpers ═══ */
  const formatDate = (dateStr) => formatPlanDate(dateStr) || '—';

  const planType = (subscriptionData?.plan_type || '').toLowerCase();
  const isTrial = planType === 'trial';
  const statusKey = (subscriptionData?.status || 'inativo').toLowerCase();
  const statusCfg = STATUS_CONFIG[statusKey] || STATUS_CONFIG.inativo;
  const benefits = PLAN_BENEFITS[planType] || [];

  /* ═══ Shared Styles (compacted) ═══ */
  const cardStyle = {
    background: 'var(--dp-bg-card)',
    border: '1px solid var(--dp-border-subtle)',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '16px',
    transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  };

  const sectionHeader = (icon, title, color = 'var(--dp-blue-500, #0DAEFF)') => (
    <div style={{
      display: 'flex', alignItems: 'center', gap: '8px',
      marginBottom: '14px', paddingBottom: '12px',
      borderBottom: '1px solid var(--dp-border-subtle)',
    }}>
      <div style={{
        width: '28px', height: '28px', borderRadius: '8px',
        background: `${color}15`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
      }}>
        {React.cloneElement(icon, { size: 14, style: { color } })}
      </div>
      <h2 style={{
        fontSize: '14px', fontWeight: 600,
        color: 'var(--dp-text-primary)',
        margin: 0,
      }}>
        {title}
      </h2>
    </div>
  );

  const inputStyle = {
    width: '100%', padding: '8px 12px', borderRadius: '6px',
    background: 'var(--dp-bg-input, #2E2E2E)',
    color: 'var(--dp-text-primary)',
    border: '1px solid var(--dp-border-subtle)',
    outline: 'none', fontSize: '13px',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s ease',
  };

  const labelStyle = {
    fontSize: '12px', color: 'var(--dp-text-secondary)',
    display: 'block', marginBottom: '6px',
  };

  // Padrão premium .dp-btn-cta (ver DESIGN_SYSTEM.md §5) — versão objeto p/ spreads.
  const primaryBtnStyle = {
    padding: '9px 18px', borderRadius: '12px',
    background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
    color: '#fff', fontSize: '13px', fontWeight: 600, letterSpacing: '0.2px',
    border: 'none', cursor: 'pointer', boxShadow: 'none',
    display: 'flex', alignItems: 'center', gap: '8px',
    fontFamily: 'var(--dp-font)',
    transition: 'all 0.2s ease',
  };

  const eyeBtnStyle = {
    position: 'absolute', right: '12px', top: '50%',
    transform: 'translateY(-50%)', background: 'none',
    border: 'none', cursor: 'pointer',
    color: 'var(--dp-text-secondary)', padding: '4px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  const handleFocus = (e) => { e.target.style.borderColor = 'var(--dp-blue-500)'; };
  const handleBlur = (e) => { e.target.style.borderColor = 'var(--dp-border-subtle)'; };

  /* ─── Password field helper ─── */
  const renderPasswordField = (label, value, onChange, show, toggleShow) => (
    <div>
      <label style={labelStyle}>{label}</label>
      <div style={{ position: 'relative' }}>
        <input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          style={inputStyle}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
        <button type="button" onClick={toggleShow} style={eyeBtnStyle}>
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
      </div>
    </div>
  );

  /* ─── Checkbox helper ─── */
  const renderCheckbox = (checked, onChange, label) => (
    <label style={{
      display: 'flex', alignItems: 'center', gap: '10px',
      cursor: 'pointer', padding: '6px 0',
    }}>
      <div
        onClick={(e) => { e.preventDefault(); onChange(!checked); }}
        style={{
          width: '18px', height: '18px', borderRadius: '5px',
          border: checked
            ? '2px solid var(--dp-blue-500, #0DAEFF)'
            : '2px solid var(--dp-border-medium, rgba(255,255,255,0.1))',
          background: checked ? 'var(--dp-blue-500, #0DAEFF)' : 'transparent',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: 'all 0.15s ease', flexShrink: 0, cursor: 'pointer',
        }}
      >
        {checked && <Check size={12} style={{ color: '#fff' }} />}
      </div>
      <span style={{ fontSize: '13px', color: 'var(--dp-text-primary)' }}>{label}</span>
    </label>
  );

  /* ═══════════════════════════ TAB RENDER FUNCTIONS ═══════════════════════════ */

  const renderPerfil = () => (
    <div className="dp-top-accent" style={cardStyle}>
      {sectionHeader(<User />, 'Perfil')}

      {/* Avatar */}
      <div style={{
        marginBottom: '16px', paddingBottom: '16px',
        borderBottom: '1px solid var(--dp-border-subtle)',
      }}>
        <AvatarUpload
          currentAvatar={profileData.avatar_url}
          onAvatarChange={handleAvatarChange}
          theme={theme}
        />
      </div>

      {/* Name + Email form */}
      <form onSubmit={handleUpdateProfile}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '12px', marginBottom: '16px',
        }}>
          <div>
            <label style={labelStyle}>Nome</label>
            <input
              type="text"
              value={profileData.full_name || ''}
              onChange={(e) => setProfileData({ ...profileData, full_name: e.target.value })}
              style={inputStyle}
              onFocus={handleFocus}
              onBlur={handleBlur}
              placeholder="Seu nome completo"
            />
          </div>
          <div>
            <label style={labelStyle}>Email</label>
            <input
              type="email"
              value={profileData.email || ''}
              disabled
              style={{ ...inputStyle, opacity: 0.5, cursor: 'not-allowed' }}
            />
            <span style={{ fontSize: '11px', color: 'var(--dp-text-tertiary)', marginTop: '3px', display: 'block' }}>
              O email não pode ser alterado
            </span>
          </div>
        </div>

        <button
          type="submit"
          disabled={profileLoading}
          style={{
            ...primaryBtnStyle,
            opacity: profileLoading ? 0.6 : 1,
            cursor: profileLoading ? 'not-allowed' : 'pointer',
          }}
        >
          <Save size={14} />
          {profileLoading ? 'Salvando...' : 'Salvar Alterações'}
        </button>
      </form>
    </div>
  );

  const renderSeguranca = () => (
    <div className="dp-top-accent" style={cardStyle}>
      {sectionHeader(<Shield />, 'Segurança')}

      <p style={{
        fontSize: '12px', color: 'var(--dp-text-secondary)',
        margin: '0 0 12px 0',
      }}>
        Alterar Senha
      </p>

      <form onSubmit={handleChangePassword}>
        <div style={{
          display: 'flex', flexDirection: 'column',
          gap: '12px', marginBottom: '16px',
        }}>
          {renderPasswordField(
            'Senha atual',
            passwordData.current_password,
            (e) => setPasswordData({ ...passwordData, current_password: e.target.value }),
            showCurrentPw, () => setShowCurrentPw(!showCurrentPw)
          )}
          {renderPasswordField(
            'Nova senha',
            passwordData.new_password,
            (e) => setPasswordData({ ...passwordData, new_password: e.target.value }),
            showNewPw, () => setShowNewPw(!showNewPw)
          )}
          {renderPasswordField(
            'Confirmar nova senha',
            passwordData.confirm_password,
            (e) => setPasswordData({ ...passwordData, confirm_password: e.target.value }),
            showConfirmPw, () => setShowConfirmPw(!showConfirmPw)
          )}
        </div>

        <button
          type="submit"
          disabled={passwordLoading}
          style={{
            ...primaryBtnStyle,
            opacity: passwordLoading ? 0.6 : 1,
            cursor: passwordLoading ? 'not-allowed' : 'pointer',
          }}
        >
          <Lock size={14} />
          {passwordLoading ? 'Alterando...' : 'Alterar Senha'}
        </button>
      </form>

      {/* Esqueci a senha atual → redefinição por e-mail (sem deslogar). */}
      <button
        type="button"
        onClick={handleForgotPassword}
        disabled={forgotLoading}
        style={{
          marginTop: '10px', background: 'none', border: 'none', padding: 0,
          color: 'var(--dp-blue-500)', fontSize: '12.5px', fontFamily: 'var(--dp-font)',
          cursor: forgotLoading ? 'default' : 'pointer', textDecoration: 'underline',
          opacity: forgotLoading ? 0.6 : 1,
        }}
      >
        {forgotLoading ? 'Enviando e-mail...' : 'Esqueci minha senha atual (receber link de redefinição por e-mail)'}
      </button>

      {/* ─── FASE 4: métodos de login (ADITIVO — criar senha / vincular Google) ─── */}
      {loginMethods && (
        <div style={{ marginTop: '22px', paddingTop: '18px', borderTop: '1px solid var(--dp-border-subtle)' }}>
          <p style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', margin: '0 0 12px 0' }}>
            Métodos de login
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--dp-text-primary)' }}>
              {loginMethods.has_password
                ? <Check size={15} style={{ color: 'var(--dp-green-500, #22C55E)' }} />
                : <X size={15} style={{ color: 'var(--dp-text-secondary)' }} />}
              Email e senha: {loginMethods.has_password ? 'ativo' : 'não definido'}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--dp-text-primary)' }}>
              {loginMethods.has_google
                ? <Check size={15} style={{ color: 'var(--dp-green-500, #22C55E)' }} />
                : <X size={15} style={{ color: 'var(--dp-text-secondary)' }} />}
              Google: {loginMethods.has_google ? 'vinculado' : 'não vinculado'}
            </div>
          </div>

          {/* Criar senha — conta Google sem senha */}
          {loginMethods.has_google && !loginMethods.has_password && (
            <form onSubmit={handleSetPassword} style={{ marginTop: '16px' }}>
              <p style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', margin: '0 0 10px 0' }}>
                Criar uma senha (para também entrar por email/senha)
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '14px' }}>
                {renderPasswordField('Nova senha', createPw.new_password,
                  (e) => setCreatePw({ ...createPw, new_password: e.target.value }),
                  showCreatePw, () => setShowCreatePw(!showCreatePw))}
                {renderPasswordField('Confirmar senha', createPw.confirm_password,
                  (e) => setCreatePw({ ...createPw, confirm_password: e.target.value }),
                  showCreatePwConfirm, () => setShowCreatePwConfirm(!showCreatePwConfirm))}
              </div>
              <button type="submit" disabled={createPwLoading}
                style={{ ...primaryBtnStyle, opacity: createPwLoading ? 0.6 : 1, cursor: createPwLoading ? 'not-allowed' : 'pointer' }}>
                <Lock size={14} />
                {createPwLoading ? 'Criando...' : 'Criar senha'}
              </button>
            </form>
          )}

          {/* Vincular Google — conta com senha, sem Google (e só se configurado) */}
          {loginMethods.has_password && !loginMethods.has_google && SETTINGS_GOOGLE_CLIENT_ID && (
            <div style={{ marginTop: '16px' }}>
              <p style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', margin: '0 0 10px 0' }}>
                Vincular sua conta Google (mesmo email) para também entrar com um clique
              </p>
              <GoogleLogin
                onSuccess={handleLinkGoogle}
                onError={() => toast.error('Não foi possível vincular o Google')}
                theme="filled_black" text="continue_with" shape="pill"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );

  const renderAPIs = () => (
    <div className="dp-top-accent" style={cardStyle}>
      {sectionHeader(<Plug />, t('settings_apis.section_title'))}
      <p style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', margin: '-6px 0 14px 0' }}>
        {t('settings_apis.section_desc')}
      </p>

      {/* ─── YouTube Data API Sub-card ─── */}
      <div style={{
        background: 'var(--dp-bg-secondary, #1C1C1C)',
        border: '1px solid var(--dp-border-subtle)',
        borderTop: '3px solid #FF0000',
        borderRadius: '10px',
        padding: '16px',
        marginBottom: '16px',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Youtube size={18} style={{ color: '#FF0000' }} />
            <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dp-text-primary)', margin: 0 }}>
              {t('settings_apis.youtube_title')}
            </h3>
          </div>
          <span style={{
            fontSize: '11px', fontWeight: 600,
            color: ytKeysConfigured ? '#34D399' : '#EF4444',
            background: ytKeysConfigured ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            border: `1px solid ${ytKeysConfigured ? 'rgba(34, 197, 94, 0.25)' : 'rgba(239, 68, 68, 0.25)'}`,
            padding: '2px 8px', borderRadius: '9999px',
          }}>
            {ytKeysConfigured
              ? `✅ ${t('settings_apis.youtube_status_configured', { count: ytKeysCount })}`
              : `❌ ${t('settings_apis.youtube_status_not_configured')}`}
          </span>
        </div>

        <p style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', margin: '0 0 12px 0' }}>
          {t('settings_apis.youtube_desc')}
        </p>

        {/* 5 key inputs */}
        {ytKeysLoading ? (
          <div style={{ height: '180px', borderRadius: '8px', background: 'var(--dp-bg-secondary)', animation: 'dp-pulse 1.5s ease infinite' }} />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '12px' }}>
            {[1, 2, 3, 4, 5].map(num => {
              const masked = ytKeysMasked[`youtube_api_key_${num}`];
              return (
                <div key={num}>
                  <label style={labelStyle}>
                    {t('settings_apis.youtube_key_label', { num })}
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showYtKeys ? 'text' : 'password'}
                      value={ytKeys[`youtube_api_key_${num}`] || ''}
                      onChange={(e) => setYtKeys(prev => ({ ...prev, [`youtube_api_key_${num}`]: e.target.value }))}
                      placeholder={masked
                        ? t('settings_apis.youtube_key_saved_placeholder', { masked })
                        : t('settings_apis.youtube_key_optional')}
                      style={{ ...inputStyle, fontFamily: "'Courier New', monospace", paddingRight: '40px' }}
                      onFocus={handleFocus}
                      onBlur={handleBlur}
                    />
                    {num === 1 && (
                      <button type="button" onClick={() => setShowYtKeys(!showYtKeys)} style={eyeBtnStyle}>
                        {showYtKeys ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Tip */}
        <div style={{
          padding: '10px', borderRadius: '8px',
          background: 'rgba(13, 174, 255, 0.06)',
          border: '1px solid rgba(13, 174, 255, 0.15)',
          marginBottom: '12px',
        }}>
          <p style={{ fontSize: '11px', color: 'var(--dp-text-secondary)', margin: 0, lineHeight: 1.6 }}>
            💡 <strong>Dica:</strong> {t('settings_apis.youtube_tip')}
          </p>
        </div>

        {/* Collapsible tutorial */}
        <button
          type="button"
          onClick={() => setShowYtTutorial(!showYtTutorial)}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--dp-blue-500)', fontSize: '12px', fontWeight: 500,
            display: 'flex', alignItems: 'center', gap: '4px',
            padding: '0', marginBottom: showYtTutorial ? '10px' : '12px',
          }}
        >
          {showYtTutorial ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          {showYtTutorial ? t('settings_apis.youtube_tutorial_hide') : t('settings_apis.youtube_tutorial_show')}
        </button>

        {showYtTutorial && (
          <div style={{
            padding: '12px', borderRadius: '10px',
            background: 'rgba(13, 174, 255, 0.06)',
            border: '1px solid rgba(13, 174, 255, 0.15)',
            marginBottom: '12px',
          }}>
            <h4 style={{ fontSize: '13px', fontWeight: 600, color: 'var(--dp-blue-500)', marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '5px' }}>
              📖 {t('settings_apis.youtube_tutorial_title')}
            </h4>
            <ol style={{ fontSize: '12px', color: 'var(--dp-text-secondary)', lineHeight: 1.8, paddingLeft: '18px', margin: 0 }}>
              <li>{t('settings_apis.youtube_step_1')} <a href="https://console.cloud.google.com/" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--dp-blue-500)', textDecoration: 'none', fontWeight: 500 }}>console.cloud.google.com</a></li>
              <li>{t('settings_apis.youtube_step_2')}</li>
              <li>{t('settings_apis.youtube_step_3')} <strong style={{ color: 'var(--dp-text-primary)' }}>"{t('settings_apis.youtube_step_3_label')}" → "{t('settings_apis.youtube_step_3_action')}"</strong></li>
              <li><strong style={{ color: 'var(--dp-text-primary)' }}>"+ {t('settings_apis.youtube_step_4_label')}"</strong> → "{t('settings_apis.youtube_step_4_action')}"</li>
              <li>{t('settings_apis.youtube_step_5')} <strong style={{ color: 'var(--dp-text-primary)' }}>"{t('settings_apis.youtube_step_5_api')}"</strong></li>
              <li>{t('settings_apis.youtube_step_6')}</li>
            </ol>
          </div>
        )}

        {/* Save button */}
        <button
          onClick={handleSaveYoutubeKeys}
          disabled={ytKeysSaving || !hasTypedYtKey}
          style={{
            ...primaryBtnStyle,
            background: hasTypedYtKey
              ? 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)'
              : 'var(--dp-border-subtle)',
            opacity: ytKeysSaving || !hasTypedYtKey ? 0.6 : 1,
            cursor: ytKeysSaving || !hasTypedYtKey ? 'not-allowed' : 'pointer',
          }}
        >
          <Save size={14} />
          {ytKeysSaving ? t('settings_apis.youtube_saving') : t('settings_apis.youtube_save')}
        </button>
      </div>

      {/* ─── Google Gemini API Sub-card ─── */}
      <div style={{
        background: 'var(--dp-bg-secondary, #1C1C1C)',
        border: '1px solid var(--dp-border-subtle)',
        borderTop: '3px solid #4285F4',
        borderRadius: '10px',
        padding: '16px',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Sparkles size={18} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />
            <h3 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--dp-text-primary)', margin: 0 }}>
              {t('settings_apis.gemini_title')}
            </h3>
          </div>
          <span style={{
            fontSize: '11px', fontWeight: 600,
            color: geminiConfigured ? '#34D399' : '#EF4444',
            background: geminiConfigured ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            border: `1px solid ${geminiConfigured ? 'rgba(34, 197, 94, 0.25)' : 'rgba(239, 68, 68, 0.25)'}`,
            padding: '2px 8px', borderRadius: '9999px',
          }}>
            {geminiConfigured ? `✅ ${t('settings_apis.gemini_status_configured')}` : `❌ ${t('settings_apis.gemini_status_not_configured')}`}
          </span>
        </div>

        {/* Info box */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: '8px',
          padding: '10px', background: 'rgba(66, 133, 244, 0.08)',
          borderRadius: '8px', marginBottom: '12px',
        }}>
          <Info size={13} style={{ color: '#4285F4', flexShrink: 0, marginTop: '1px' }} />
          <p style={{ fontSize: '11px', color: 'var(--dp-text-secondary)', margin: 0, lineHeight: 1.5 }}>
            {t('settings_apis.gemini_info')}
          </p>
        </div>

        {geminiLoading ? (
          <div style={{ height: '80px', borderRadius: '8px', background: 'var(--dp-bg-secondary)', animation: 'dp-pulse 1.5s ease infinite' }} />
        ) : (
          <>
            {/* API Key field */}
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>{t('settings_apis.gemini_key_label')}</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showGeminiKey ? 'text' : 'password'}
                  value={geminiKey || ''}
                  onChange={(e) => setGeminiKey(e.target.value)}
                  placeholder="AIza..."
                  style={{ ...inputStyle, fontFamily: "'Courier New', monospace", paddingRight: '40px' }}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                />
                <button type="button" onClick={() => setShowGeminiKey(!showGeminiKey)} style={eyeBtnStyle}>
                  {showGeminiKey ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              <a
                href="https://aistudio.google.com/apikey"
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontSize: '11px', color: '#4285F4', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '3px', marginTop: '4px' }}
              >
                {t('settings_apis.gemini_get_key')} <ExternalLink size={11} />
              </a>
            </div>

            {/* Model selector */}
            <div style={{ marginBottom: '12px' }}>
              <label style={labelStyle}>{t('settings_apis.gemini_model_label')}</label>
              <CustomDropdown
                value={geminiModel}
                onChange={(val) => setGeminiModel(val)}
                options={[
                  { value: '', label: t('settings_apis.gemini_model_auto') },
                  ...GEMINI_MODELS.map(m => ({ value: m.id, label: `${m.name} — ${m.desc}` })),
                ]}
                placeholder={t('settings_apis.gemini_model_auto')}
              />
            </div>

            {/* Test result */}
            {geminiTestResult && (
              <div style={{
                marginBottom: '12px', padding: '10px', borderRadius: '8px',
                background: geminiTestResult.success ? 'rgba(34, 197, 94, 0.08)' : 'rgba(239, 68, 68, 0.08)',
                border: `1px solid ${geminiTestResult.success ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)'}`,
                display: 'flex', alignItems: 'center', gap: '6px',
              }}>
                {geminiTestResult.success ? <Check size={13} style={{ color: '#34D399' }} /> : <X size={13} style={{ color: '#EF4444' }} />}
                <span style={{ fontSize: '12px', color: geminiTestResult.success ? '#34D399' : '#EF4444' }}>
                  {geminiTestResult.success ? t('settings_apis.gemini_test_ok', { model: geminiTestResult.model }) : geminiTestResult.error}
                </span>
              </div>
            )}

            {/* Buttons */}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                onClick={handleTestGemini}
                disabled={geminiTesting || !geminiKey}
                style={{
                  ...primaryBtnStyle,
                  background: 'transparent',
                  border: '1px solid var(--dp-border-subtle)',
                  color: 'var(--dp-text-primary)',
                  opacity: geminiTesting || !geminiKey ? 0.5 : 1,
                  cursor: geminiTesting || !geminiKey ? 'not-allowed' : 'pointer',
                }}
              >
                {geminiTesting ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <TestTube size={14} />}
                {geminiTesting ? t('settings_apis.gemini_testing') : t('settings_apis.gemini_test')}
              </button>
              <button
                onClick={handleSaveGemini}
                disabled={geminiSaving}
                style={{
                  ...primaryBtnStyle,
                  opacity: geminiSaving ? 0.6 : 1,
                  cursor: geminiSaving ? 'not-allowed' : 'pointer',
                }}
              >
                <Save size={14} />
                {geminiSaving ? t('settings_apis.gemini_saving') : t('settings_apis.gemini_save')}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const renderPreferencias = () => (
    <>
      {/* Preferências card */}
      <div className="dp-top-accent" style={cardStyle}>
        {sectionHeader(<Globe />, 'Preferências')}

        {/* Language + Theme grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: '12px', marginBottom: '16px',
        }}>
          {/* Language */}
          <div>
            <label style={labelStyle}>Idioma</label>
            <select
              value={i18n.language?.substring(0, 2) || 'pt'}
              onChange={(e) => i18n.changeLanguage(e.target.value)}
              style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
            >
              <option value="pt">🇧🇷 Português</option>
              <option value="en">🇺🇸 English</option>
              <option value="es">🇪🇸 Español</option>
            </select>
          </div>

          {/* Theme */}
          <div>
            <label style={labelStyle}>Tema</label>
            <select
              value={isDarkMode ? 'dark' : 'light'}
              onChange={(e) => {
                const wantDark = e.target.value === 'dark';
                if (wantDark !== isDarkMode) toggleTheme();
              }}
              style={{ ...inputStyle, cursor: 'pointer', appearance: 'auto' }}
            >
              <option value="dark">🌙 Escuro</option>
              <option value="light">☀️ Claro</option>
            </select>
          </div>
        </div>

        {/* Notifications */}
        <div>
          <label style={{ ...labelStyle, marginBottom: '10px' }}>Notificações</label>
          {renderCheckbox(
            notifNovidades,
            (v) => handleNotifChange('novidades', v),
            'Receber emails sobre novidades'
          )}
          {renderCheckbox(
            notifLembretes,
            (v) => handleNotifChange('lembretes', v),
            'Receber lembretes de vídeos agendados'
          )}
        </div>
      </div>

      {/* Meu Plano card (compact) */}
      <div className="dp-top-accent" style={cardStyle}>
        {sectionHeader(<CreditCard />, 'Meu Plano')}

        {isLoading ? (
          <div style={{
            height: '44px', borderRadius: '8px',
            background: 'var(--dp-bg-secondary, #1C1C1C)',
            animation: 'dp-pulse 1.5s ease infinite',
          }} />
        ) : (
          <>
            {/* Plan summary row */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: '12px',
            }}>
              {/* Left: icon + name + status */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '10px',
                  background: 'linear-gradient(135deg, var(--dp-blue-500, #0DAEFF), #3B82F6)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '18px', flexShrink: 0,
                }}>
                  {getPlanIcon(planType)}
                </div>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      fontSize: '14px', fontWeight: 700,
                      color: 'var(--dp-text-primary)',
                    }}>
                      {isSuperAdmin ? 'ADMIN' : (subscriptionData?.plan_type || 'Carregando').toUpperCase()}
                    </span>
                    {!isSuperAdmin && (
                      <span style={{
                        fontSize: '10px', fontWeight: 600,
                        color: statusCfg.color, background: statusCfg.bg,
                        padding: '2px 8px', borderRadius: '9999px',
                        border: `1px solid ${statusCfg.border}`,
                      }}>
                        {statusCfg.label}
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: '12px', color: 'var(--dp-text-secondary)' }}>
                    {isSuperAdmin
                      ? 'Acesso total ao sistema'
                      : isTrial
                        ? `Teste gratuito • Expira em ${formatDate(subscriptionData?.end_date)}`
                        : `Mensal • Renova em ${formatDate(subscriptionData?.next_charge_date || subscriptionData?.end_date)}`
                    }
                  </span>
                </div>
              </div>

              {/* Right: link — só premium/admin vai gerenciar o plano; free não é levado à compra */}
              {(isSuperAdmin || (subscriptionData?.has_subscription && !isTrial)) && (
                <Link to="/meu-plano" style={{
                  fontSize: '12px', fontWeight: 500,
                  color: 'var(--dp-text-secondary)',
                  textDecoration: 'none',
                  display: 'flex', alignItems: 'center', gap: '4px',
                  transition: 'color 0.2s',
                }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'var(--dp-blue-500, #0DAEFF)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = 'var(--dp-text-secondary)'}
                >
                  Ver detalhes do plano
                  <ExternalLink size={12} />
                </Link>
              )}
            </div>

            {/* Benefits chips */}
            {benefits.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '12px' }}>
                {benefits.map((b, i) => (
                  <span key={i} style={{
                    fontSize: '10px', color: 'var(--dp-text-secondary)',
                    background: 'rgba(13, 174, 255, 0.06)',
                    border: '1px solid rgba(13, 174, 255, 0.12)',
                    padding: '3px 10px', borderRadius: '9999px',
                    display: 'flex', alignItems: 'center', gap: '3px',
                  }}>
                    <Check size={9} style={{ color: '#34D399' }} />
                    {b}
                  </span>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );

  const renderAvancado = () => (
    <>
      {/* Zona de Perigo card */}
      <div style={{
        ...cardStyle,
        border: '1px solid rgba(239, 68, 68, 0.25)',
      }}>
        {sectionHeader(<AlertTriangle />, 'Zona de Perigo', '#F87171')}

        <div style={{ marginBottom: '12px' }}>
          <h4 style={{
            fontSize: '13px', fontWeight: 600,
            color: 'var(--dp-text-primary)', margin: '0 0 4px 0',
          }}>
            Resetar Dados
          </h4>
          <p style={{
            fontSize: '12px', color: 'var(--dp-text-secondary)',
            lineHeight: 1.6, margin: 0,
          }}>
            Remove permanentemente todos os canais, vídeos, roteiros, inspirações e prompts.
            Sua conta de usuário será preservada.
          </p>
        </div>

        <button
          onClick={() => setShowResetModal(true)}
          style={{
            padding: '8px 16px', borderRadius: '8px',
            background: 'transparent',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#F87171', fontSize: '13px', fontWeight: 500,
            cursor: 'pointer', transition: 'all 0.2s ease',
            display: 'flex', alignItems: 'center', gap: '6px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(239, 68, 68, 0.08)';
            e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.5)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.borderColor = 'rgba(239, 68, 68, 0.3)';
          }}
        >
          <AlertTriangle size={14} />
          Resetar Sistema
        </button>
      </div>

      {/* Sobre o Sistema card */}
      <div className="dp-top-accent" style={{ ...cardStyle, marginBottom: 0 }}>
        {sectionHeader(<Info />, 'Sobre o Sistema')}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {[
            { label: 'Nome', value: 'Dark Planner' },
            { label: 'Versão', value: '3.0' },
            { label: 'Última atualização', value: 'Fevereiro 2026' },
          ].map((item, i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: 'space-between',
              fontSize: '13px', padding: '3px 0',
            }}>
              <span style={{ color: 'var(--dp-text-secondary)' }}>{item.label}</span>
              <span style={{ color: 'var(--dp-text-primary)', fontWeight: 500 }}>{item.value}</span>
            </div>
          ))}
        </div>
      </div>
    </>
  );

  /* ═══════════════════════════════ RENDER ═══════════════════════════════ */
  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: 'var(--dp-bg-primary)' }}>
      <Sidebar onLogout={onLogout} />

      <div style={{
        marginLeft: isMobile ? 0 : `${sidebarWidth}px`,
        flex: 1, minWidth: 0, transition: 'margin-left 0.3s ease',
        display: 'flex', flexDirection: 'column',
      }}>
        <Header />

        <main className="dp-main-content" style={{
          marginTop: '60px', flex: 1,
          animation: 'dp-pageFadeIn 0.35s ease',
        }}>
          <PageHeader
            icon={<Settings size={20} style={{ color: 'var(--dp-blue-500, #0DAEFF)' }} />}
            title="Configurações"
            description="Gerencie seu perfil, segurança e preferências"
          />

          {/* ════════════════ TAB BAR ════════════════ */}
          <div style={{
            display: 'flex',
            gap: '8px',
            marginBottom: '20px',
            overflowX: 'auto',
            paddingBottom: '4px',
          }}>
            {TABS.map(tab => {
              const active = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    borderRadius: '10px',
                    background: active ? 'rgba(20, 144, 255, 0.1)' : 'transparent',
                    color: active ? 'var(--dp-blue-500)' : 'var(--dp-text-secondary)',
                    border: active ? '1px solid rgba(20, 144, 255, 0.3)' : '1px solid transparent',
                    fontSize: '14px',
                    fontWeight: active ? 600 : 400,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    whiteSpace: 'nowrap',
                    fontFamily: 'var(--dp-font)',
                  }}
                >
                  {tab.icon} {tab.label}
                </button>
              );
            })}
          </div>

          {/* ════════════════ TAB CONTENT ════════════════ */}
          <div key={activeTab} style={{ animation: 'dp-fadeInUp 0.3s ease' }}>
            {activeTab === 'perfil' && renderPerfil()}
            {activeTab === 'seguranca' && renderSeguranca()}
            {activeTab === 'apis' && renderAPIs()}
            {activeTab === 'preferencias' && renderPreferencias()}
            {activeTab === 'avancado' && renderAvancado()}
          </div>
        </main>
      </div>

      {/* ════════════════ RESET MODAL ════════════════ */}
      {showResetModal && (
        <div
          onClick={() => { setShowResetModal(false); setResetConfirmText(''); }}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 10000, padding: '16px',
            animation: 'resetModalOverlayIn 0.25s ease',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'var(--dp-bg-glass, rgba(28, 28, 28, 0.95))',
              backdropFilter: 'blur(24px)',
              WebkitBackdropFilter: 'blur(24px)',
              border: '1px solid var(--dp-border-medium, rgba(255,255,255,0.1))',
              borderRadius: '16px',
              width: '480px', maxWidth: '94vw',
              padding: '24px',
              animation: 'resetModalSlideUp 0.3s ease',
            }}
          >
            {/* Modal header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: '16px',
            }}>
              <h2 style={{
                fontSize: '18px', fontWeight: 600, color: '#F87171',
                margin: 0, display: 'flex', alignItems: 'center', gap: '8px',
              }}>
                <AlertTriangle size={20} />
                Resetar todos os dados?
              </h2>
              <button
                onClick={() => { setShowResetModal(false); setResetConfirmText(''); }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: 'var(--dp-text-secondary)', padding: '4px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '6px', transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.color = 'var(--dp-text-primary)'}
                onMouseLeave={(e) => e.currentTarget.style.color = 'var(--dp-text-secondary)'}
              >
                <X size={20} />
              </button>
            </div>

            {/* Warning */}
            <div style={{
              background: 'rgba(248, 113, 113, 0.08)',
              border: '1px solid rgba(248, 113, 113, 0.2)',
              borderRadius: '12px', padding: '14px',
              fontSize: '13px', color: 'var(--dp-text-secondary)',
              marginBottom: '16px', lineHeight: 1.6,
            }}>
              <p style={{ fontWeight: 600, color: '#F87171', marginBottom: '8px', marginTop: 0 }}>
                Esta ação é irreversível. Serão apagados:
              </p>
              <ul style={{ margin: 0, paddingLeft: '18px' }}>
                <li>Canais</li>
                <li>Vídeos</li>
                <li>Roteiros</li>
                <li>Prompts</li>
                <li>Referências</li>
              </ul>
              <p style={{ marginTop: '10px', marginBottom: 0, fontStyle: 'italic' }}>
                Sua conta de usuário será preservada.
              </p>
            </div>

            {/* Confirm input */}
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>
                Digite <strong style={{ color: '#F87171' }}>RESETAR</strong> para confirmar:
              </label>
              <input
                type="text"
                value={resetConfirmText}
                onChange={(e) => setResetConfirmText(e.target.value)}
                placeholder="RESETAR"
                style={{
                  ...inputStyle,
                  borderColor: resetConfirmText === 'RESETAR' ? '#34D399' : undefined,
                }}
                autoFocus
              />
            </div>

            {/* Action buttons */}
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => { setShowResetModal(false); setResetConfirmText(''); }}
                style={{
                  padding: '10px 20px', borderRadius: '10px',
                  background: 'transparent', color: 'var(--dp-text-secondary)',
                  border: '1px solid var(--dp-border-subtle)',
                  cursor: 'pointer', fontSize: '14px', fontWeight: 500,
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--dp-bg-card)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                Cancelar
              </button>
              <button
                onClick={handleResetData}
                disabled={resetConfirmText !== 'RESETAR' || resetting}
                style={{
                  padding: '10px 20px', borderRadius: '10px',
                  background: resetConfirmText === 'RESETAR' && !resetting ? '#EF4444' : 'var(--dp-border-subtle)',
                  color: '#fff', fontSize: '14px', fontWeight: 600,
                  border: 'none',
                  cursor: resetConfirmText === 'RESETAR' && !resetting ? 'pointer' : 'not-allowed',
                  opacity: resetConfirmText === 'RESETAR' && !resetting ? 1 : 0.5,
                  transition: 'all 0.2s',
                  display: 'flex', alignItems: 'center', gap: '8px',
                }}
              >
                {resetting ? (
                  <>
                    <div style={{
                      width: '14px', height: '14px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid #fff',
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite',
                    }} />
                    Resetando...
                  </>
                ) : (
                  'Resetar Sistema'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Keyframes ─── */}
      <style>{`
        @keyframes resetModalOverlayIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes resetModalSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.98); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes dp-pulse {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 0.3; }
        }
      `}</style>
    </div>
  );
};

export default ConfiguracoesNew;
